import React, { useState } from "react";
import { api, fileUrl, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Tile } from "./AdminTable";
import { UserModal } from "./UserModal";

const STATUSES = ["collecting", "ready", "validated", "in_production", "delivered"];
const STATUS_LABELS = {
    collecting: "Collecte en cours",
    ready: "Prêt (seuil atteint)",
    validated: "Validé pour production",
    in_production: "En production",
    delivered: "Livré",
};
const STATUS_CLS = {
    delivered: "border-green-700 text-green-400",
    in_production: "border-orange-500/40 text-orange-500",
    ready: "border-yellow-500/60 text-yellow-400 bg-yellow-500/10",
    validated: "border-blue-500/40 text-blue-400",
};
function statusCls(s) { return STATUS_CLS[s] || "border-zinc-700 text-zinc-300"; }

export function OrderDetail({ order, onBack, onAction }) {
    const j = order.jersey;
    const c = order.club;
    const sponsorships = order.sponsorships || [];
    const [status, setStatus] = useState(j.status);
    const [saving, setSaving] = useState(false);
    const [openSponsor, setOpenSponsor] = useState(null);
    const [openUser, setOpenUser] = useState(null);

    const numPlayers = sponsorships[0]?.num_players || 0;
    const htPerJersey = j.total_collected;
    const totalHT = htPerJersey * numPlayers;
    const tva = Math.round(totalHT * 20) / 100;
    const totalTTC = Math.round((totalHT * 1.2) * 100) / 100;

    const changeStatus = async (next) => {
        setSaving(true);
        try {
            await api.post(`/admin/orders/${j.id}/status?status=${next}`);
            toast.success("Statut mis à jour");
            setStatus(next);
            onAction?.(j.id, "refresh");
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm("Supprimer définitivement cette commande et ses sponsorings ?")) return;
        try {
            await api.delete(`/admin/orders/${j.id}`);
            toast.success("Commande supprimée");
            onBack?.();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const openSponsorInfo = async (sp) => {
        setOpenSponsor(sp.id);
        try {
            const { data } = await api.get(`/admin/users/${sp.sponsor_id}`);
            setOpenUser(data);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
            setOpenSponsor(null);
        }
    };

    return (
        <div className="space-y-6">
            <button onClick={onBack} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-orange-500">
                ← Retour
            </button>
            <div className="card-tu">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-1">
                            Commande
                        </div>
                        <h2 className="font-display text-4xl uppercase tracking-tight">
                            {c?.name} — {j.age_category}
                        </h2>
                        <div className="text-zinc-400 text-sm mt-2">
                            {c?.city} · {c?.department} · {c?.sport} · {c?.level}
                        </div>
                    </div>
                    <span className={`chip ${statusCls(status)}`}>{STATUS_LABELS[status] || status}</span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mb-6">
                    <Tile label="Total/maillot HT" value={`${htPerJersey}€`} />
                    <Tile label="Maillots" value={numPlayers} />
                    <Tile label="Sponsors" value={sponsorships.length} />
                    <Tile label="Statut" value={STATUS_LABELS[status] || status} />
                </div>

                {/* HT / TVA / TTC breakdown */}
                <div className="border border-zinc-800 bg-zinc-950 p-4 mb-6" data-testid="order-totals">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-3">
                        Détail comptable
                    </div>
                    <div className="space-y-2 text-sm">
                        <Row
                            label={`Total collecté/maillot × ${numPlayers} maillot${numPlayers > 1 ? "s" : ""}`}
                            value={`${formatEuro(totalHT)} HT`}
                            testid="order-total-ht"
                        />
                        <Row
                            label="TVA 20%"
                            value={`+ ${formatEuro(tva)}`}
                            testid="order-total-tva"
                            muted
                        />
                        <div className="border-t border-zinc-800 pt-2">
                            <Row
                                label="Total TTC"
                                value={`${formatEuro(totalTTC)} TTC`}
                                testid="order-total-ttc"
                                bold
                            />
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                    <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Modifier le statut</label>
                    <select
                        data-testid="order-status-select"
                        value={status}
                        disabled={saving}
                        onChange={(e) => changeStatus(e.target.value)}
                        className="input-tu w-auto py-2"
                    >
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                        ))}
                    </select>
                    <button
                        data-testid="order-delete"
                        onClick={remove}
                        className="px-3 py-2 border border-red-900 hover:bg-red-950 text-red-400 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1 ml-auto"
                    >
                        <Trash2 size={14} /> Supprimer
                    </button>
                </div>

                <h3 className="font-display text-2xl uppercase tracking-tight mb-4">
                    Sponsors ({sponsorships.length})
                </h3>
                {sponsorships.length === 0 ? (
                    <div className="text-zinc-500 text-sm italic">Aucun sponsor.</div>
                ) : (
                    <div className="space-y-2">
                        {sponsorships.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => openSponsorInfo(s)}
                                disabled={openSponsor === s.id}
                                data-testid={`order-sponsor-row-${s.id}`}
                                className="w-full text-left border border-zinc-800 p-3 flex flex-wrap items-center gap-4 bg-zinc-950 hover:bg-zinc-900/50 hover:border-orange-500 transition-colors"
                            >
                                <div className="bg-zinc-900 border border-zinc-800 w-16 h-16 flex items-center justify-center shrink-0">
                                    <img src={fileUrl(s.logo_path)} alt={s.sponsor_company} className="max-h-12 max-w-full object-contain" />
                                </div>
                                <div className="flex-1 min-w-[200px]">
                                    <div className="font-bold text-orange-500 underline-offset-2 hover:underline">{s.sponsor_company}</div>
                                    <div className="text-xs text-zinc-500 uppercase tracking-widest">
                                        {s.zones?.map((z) => z.name).join(", ")}
                                    </div>
                                    <div className="text-[11px] text-zinc-500">
                                        {s.num_players} maillots × {s.per_jersey_amount}€
                                        {s.discount_pct > 0 && ` · −${s.discount_pct}%`}
                                    </div>
                                </div>
                                <div className="font-display text-2xl text-orange-500">{s.total_amount}€</div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {openUser && (
                <UserModal
                    user={openUser}
                    onClose={() => { setOpenUser(null); setOpenSponsor(null); }}
                    onSaved={() => { setOpenUser(null); setOpenSponsor(null); onAction?.(j.id, "refresh"); }}
                />
            )}
        </div>
    );
}

function formatEuro(n) {
    return `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
}

function Row({ label, value, testid, bold, muted }) {
    return (
        <div className="flex items-center justify-between gap-4">
            <span className={[
                "text-zinc-300",
                bold ? "font-bold uppercase tracking-widest text-sm" : "",
                muted ? "text-zinc-500 text-xs uppercase tracking-widest" : "",
            ].join(" ")}>{label}</span>
            <span
                data-testid={testid}
                className={[
                    bold ? "font-display text-2xl text-orange-500" : "",
                    muted ? "text-zinc-400 text-sm" : "text-zinc-100",
                ].join(" ")}
            >
                {value}
            </span>
        </div>
    );
}
